import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { UploadCloud, FileWarning, RefreshCw, Archive, RotateCw } from "lucide-react";

// Imports page — ProRoast .sqlite uploads.
// The backend ingests in a background asyncio task so the HTTP response
// returns immediately (avoiding Cloudflare's 100s edge timeout on the
// previously-synchronous endpoint). Progress is polled from /imports/active.
export default function Imports() {
  const [logs, setLogs] = useState([]);
  const [archives, setArchives] = useState([]);
  const [active, setActive] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [urlInput, setUrlInput] = useState("");
  const [urlBusy, setUrlBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/imports"),
        api.get("/imports/archives"),
        api.get("/imports/active"),
      ]);
      setLogs(a.data);
      setArchives(b.data);
      setActive(c.data);
    } catch (e) { /* ignore — page still works without */ }
  };
  useEffect(() => { load(); }, []);

  // Poll active status every 3 s while a job is running
  useEffect(() => {
    if (!active?.running) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/imports/active");
        setActive(data);
        if (!data.running) {
          await load();
        }
      } catch (e) { /* keep trying */ }
    }, 3000);
    return () => clearInterval(t);
  }, [active?.running]);

  const upload = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    setUploadPct(0);

    // Cloudflare's free tier rejects request bodies >100 MB. Always use
    // chunked upload — the backend assembles before ingest. Each chunk is
    // small enough to never hit any edge body-size limit.
    const CHUNK = 8 * 1024 * 1024; // 8 MB
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));
    const uploadId = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/-/g, "");

    try {
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK;
        const end = Math.min(file.size, start + CHUNK);
        const slice = file.slice(start, end);
        const fd = new FormData();
        fd.append("file", slice, file.name);
        await api.post(
          `/imports/chunk?upload_id=${uploadId}&chunk_index=${i}&total_chunks=${totalChunks}&filename=${encodeURIComponent(file.name)}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
      }
      // Last chunk triggers backend assembly + ingest; switch to status-poll mode
      const { data } = await api.get("/imports/active");
      setActive(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const reingest = async (archiveId) => {
    setError("");
    try {
      await api.post(`/imports/archives/${archiveId}/reingest`);
      const { data } = await api.get("/imports/active");
      setActive(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Server-side download from a public URL — bypasses Cloudflare's 100 MB
  // body cap entirely. Best for 200–300 MB ProRoast cumulative exports
  // that won't fit through residential-link chunked uploads.
  const fetchFromUrl = async () => {
    if (!urlInput.trim()) return;
    setError(""); setUrlBusy(true);
    try {
      await api.post("/imports/from-url", { url: urlInput.trim() });
      setUrlInput("");
      const { data } = await api.get("/imports/active");
      setActive(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUrlBusy(false); }
  };

  // Recovery action: re-run ONLY the curves backfill step against the
  // current DB. Useful when an earlier import inserted roasts but a
  // silently-failed curves step left those roasts without graphs.
  // Async: the backend returns immediately, then the active-status
  // poller picks up phase=complete + result. The result is shown in
  // the same banner as a normal upload (with curves-specific stats).
  const forceBackfillCurves = async () => {
    if (!urlInput.trim()) {
      setError("Paste the Drive URL of the latest .sqlite first, then click Backfill curves.");
      return;
    }
    setError(""); setBackfillBusy(true);
    try {
      await api.post("/imports/force-backfill-curves", { url: urlInput.trim() });
      const { data } = await api.get("/imports/active");
      setActive(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBackfillBusy(false); }
  };

  const phaseLabel = (phase) => ({
    ingesting: "Parsing & inserting roasts",
    archiving: "Saving archive copy",
    complete: "Done",
    error: "Failed",
  }[phase] || phase || "Working");

  return (
    <div data-testid="imports-page">
      <PageHeader
        eyebrow="Operations"
        title="ProRoast Imports"
        description="Upload your daily ProRoast .sqlite backup OR a .csv export of the RoastProfile table. Duplicates are auto-skipped — the file can be cumulative."
      />

      <div className="bg-card border border-dashed border-primary/40 rounded p-8 mb-4 text-center">
        <UploadCloud className="w-10 h-10 text-primary mx-auto mb-3" strokeWidth={1.5} />
        <p className="font-display text-xl font-bold mb-1">Drop or choose a .sqlite or .csv file</p>
        <p className="text-sm text-muted-foreground mb-4">
          Cumulative file — only new roasts get added. Up to ~95 MB direct upload. File is archived so it can be re-ingested later. CSV = profiles only (no curves).
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".sqlite,.csv"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="imports-file-input"
        />
        <button
          disabled={uploading || active?.running || urlBusy}
          onClick={() => inputRef.current?.click()}
          data-testid="imports-upload-button"
          className="px-5 py-2.5 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60"
        >
          {uploading ? `Uploading… ${uploadPct}%` : (active?.running ? "Import in progress…" : "Choose file")}
        </button>
        {error && <p className="text-destructive text-sm mt-3" data-testid="imports-error">{error}</p>}
      </div>

      {/* Server-side download — works for files of any size (bypasses Cloudflare cap) */}
      <div className="bg-card border border-border rounded p-5 mb-6">
        <p className="label-tiny mb-2">Or paste a Google Drive / public URL — best for files over ~100 MB</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            placeholder="https://drive.google.com/file/d/.../view  ·  or any public .sqlite URL"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            disabled={urlBusy || backfillBusy || active?.running}
            data-testid="imports-url-input"
            className="flex-1 px-3 py-2 bg-white border border-border rounded text-sm focus:outline-none focus:border-primary"
          />
          <button
            onClick={fetchFromUrl}
            disabled={!urlInput.trim() || urlBusy || backfillBusy || active?.running}
            data-testid="imports-url-fetch"
            className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60"
          >
            {urlBusy ? "Starting…" : "Download & ingest"}
          </button>
          <button
            onClick={forceBackfillCurves}
            disabled={!urlInput.trim() || backfillBusy || urlBusy || active?.running}
            data-testid="imports-backfill-curves"
            title="Re-run JUST the curves step against the current DB. Use this if some roasts show 'No curve data captured'."
            className="px-4 py-2 bg-secondary border border-border rounded text-sm font-semibold hover:bg-secondary/70 disabled:opacity-60"
          >
            {backfillBusy ? "Backfilling…" : "Backfill curves only"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          For Drive: right-click the .sqlite file → "Share" → "Anyone with the link" → copy link → paste here.
          The backend downloads it directly, so your browser doesn't need to upload anything.
        </p>
      </div>

      {/* Active job */}
      {active?.running && (
        <div className="bg-primary/10 border border-primary/30 rounded p-4 mb-6 flex items-start gap-3" data-testid="imports-active">
          <RefreshCw className="w-5 h-5 animate-spin text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold">{active.filename || "Import running"}</p>
            <p className="text-sm">
              {phaseLabel(active.phase)}
              {active.started_at && (
                <span className="text-muted-foreground"> · started {new Date(active.started_at).toLocaleTimeString("en-ZA")}</span>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              You can leave this page — the import keeps running on the server.
            </p>
          </div>
        </div>
      )}

      {active && !active.running && active.phase === "complete" && active.result && (
        <div className="bg-card border border-[hsl(132,18%,36%)] rounded p-5 mb-6" data-testid="imports-result">
          <p className="label-tiny" style={{ color: "hsl(132 18% 36%)" }}>Last import complete</p>
          <h3 className="font-display text-xl font-bold mt-0.5 mb-3">{active.filename}</h3>

          {/* Curves backfill result has a different shape than a normal ingest. */}
          {typeof active.result.curves_in_file === "number" && active.filename === "Curves backfill" ? (
            <div data-testid="imports-backfill-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Stat label="Curves in file" value={active.result.curves_in_file} />
                <Stat label="Matched (UID)" value={active.result.roasts_matched_by_source_uid} />
                <Stat label="Matched (dedup key)" value={active.result.roasts_matched_by_dedup_key} />
                <Stat label="Curves added" value={active.result.curves_added} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                <Stat label="Already had curves" value={active.result.curves_already_present} />
                <Stat label="To insert (planned)" value={active.result.curves_to_insert} />
                <Stat label="Roasts in file not in DB" value={active.result.roasts_in_file_not_in_db} />
              </div>
              {active.result.roasts_matched_by_dedup_key > 0 && (
                <p className="text-xs text-muted-foreground mt-3">
                  Some roasts had their source_uid renumbered (likely a ProRoast tablet reset). Curves were matched by dedup key instead.
                </p>
              )}
              {active.result.roasts_in_file_not_in_db > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {active.result.roasts_in_file_not_in_db} roast(s) in the file aren't in the DB yet. Use "Download & ingest" first if you want them imported.
                  {active.result.sample_missing_source_uids?.length > 0 &&
                    ` (Sample UIDs: ${active.result.sample_missing_source_uids.join(", ")})`}
                </p>
              )}
              {active.result.insert_errors?.length > 0 && (
                <div className="mt-3 text-destructive text-xs">
                  <p className="font-semibold">Insert errors:</p>
                  {active.result.insert_errors.map((e, i) => (
                    <p key={i} className="font-mono break-all">{e}</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <Stat label="Total in file" value={active.result.total_in_file} />
              <Stat label="Newly added" value={active.result.new_count} />
              <Stat label="Already imported" value={active.result.skipped_count} />
              <Stat label="New origins" value={active.result.new_origins} />
              <Stat label="Curves added" value={active.result.curves_added} />
            </div>
          )}
        </div>
      )}

      {active && !active.running && active.phase === "error" && (
        <div className="bg-destructive/10 border border-destructive/40 rounded p-4 mb-6" data-testid="imports-failed">
          <p className="label-tiny text-destructive">Last import failed</p>
          <p className="text-sm mt-1">{active.error}</p>
        </div>
      )}

      {/* Archived .sqlite — one per upload (latest replaces previous) */}
      {archives.length > 0 && (
        <div className="bg-card border border-border rounded mb-6">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Archive className="w-4 h-4" />
            <p className="label-tiny">Archived .sqlite</p>
          </div>
          <div className="divide-y divide-border">
            {archives.map((a) => (
              <div key={a.id} className="px-4 py-3 flex items-center gap-3" data-testid={`archive-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {(a.size_bytes / (1024 * 1024)).toFixed(1)} MB · uploaded {new Date(a.uploaded_at).toLocaleString("en-ZA")} by {a.uploaded_by}
                  </p>
                </div>
                <button
                  onClick={() => reingest(a.id)}
                  disabled={active?.running}
                  data-testid={`archive-reingest-${a.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded text-xs hover:bg-secondary disabled:opacity-50"
                >
                  <RotateCw className="w-3 h-3" /> Re-ingest
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="font-display text-xl font-bold mb-3">Import history</h3>
      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="imports-table">
          <thead>
            <tr className="bg-secondary">
              <th className="label-tiny px-4 py-3 text-left">When</th>
              <th className="label-tiny px-4 py-3 text-left">File</th>
              <th className="label-tiny px-4 py-3 text-right">In file</th>
              <th className="label-tiny px-4 py-3 text-right">New</th>
              <th className="label-tiny px-4 py-3 text-right">Skipped</th>
              <th className="label-tiny px-4 py-3 text-right">Curves</th>
              <th className="label-tiny px-4 py-3 text-left">By</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                <FileWarning className="w-6 h-6 mx-auto mb-2 opacity-50" />
                No imports yet.
              </td></tr>
            )}
            {logs.map((l) => {
              const isBackfill = (l.filename || "").startsWith("Curves backfill");
              const br = l.backfill_result;
              const tipParts = [];
              if (isBackfill && br) {
                tipParts.push(`Curves in file: ${br.curves_in_file ?? "?"}`);
                tipParts.push(`Matched (UID): ${br.roasts_matched_by_source_uid ?? 0}`);
                tipParts.push(`Matched (dedup): ${br.roasts_matched_by_dedup_key ?? 0}`);
                tipParts.push(`Already had curves: ${br.curves_already_present ?? 0}`);
                tipParts.push(`Roasts in file not in DB: ${br.roasts_in_file_not_in_db ?? 0}`);
                if (br.note) tipParts.push(`Note: ${br.note}`);
                if (br.insert_errors?.length) tipParts.push(`Insert errors: ${br.insert_errors.join("; ")}`);
              }
              if (l.curves_error) tipParts.push(`Error: ${l.curves_error}`);
              return (
                <tr key={l.id} className="border-t border-border hover:bg-secondary/40" title={tipParts.join("\n")}>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.finished_at ? new Date(l.finished_at).toLocaleString("en-ZA") : "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs break-all">{l.filename}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{l.total_in_file ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[hsl(132,18%,36%)]">{l.new_count ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{l.skipped_count ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {l.curves_error ? (
                      <span className="text-destructive font-semibold" data-testid="curves-error-cell">err</span>
                    ) : (typeof l.curves_added === "number" ? l.curves_added : "—")}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{l.triggered_by}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="label-tiny mb-1">{label}</p>
      <p className="font-display text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}
